import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Button from "../../../../components/Button";
import Input, { MaterialInputEvent } from "../../../../components/v2/Input";
import Layout from "../../../../components/v2/Layout";
import AuthService, {
  CLIENT,
  isAuthenticated,
  UID,
  USER,
} from "../../../../services/AuthService";

import style from "../../Login/styles.module.scss";

const keyI18n = "translations.login";
const adminI18n = "translations.admin.login";
const SUPPORT_EMAIL = "suporte@pgm.fortaleza.ce.gov.br";

type FormError = "credentials" | "forbidden" | "unavailable" | null;

export default function AdminLogin() {
  const { t } = useTranslation();
  const history = useHistory();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [formError, setFormError] = useState<FormError>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      return;
    }

    AuthService.adminProfile()
      .then(() => history.replace("/admin"))
      .catch(() => undefined);
  }, [history]);

  const clearErrors = () => {
    setEmailError(false);
    setPasswordError(false);
    setFormError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearErrors();

    const emptyEmail = email.trim() === "";
    const emptyPassword = password.trim() === "";

    if (emptyEmail || emptyPassword) {
      setEmailError(emptyEmail);
      setPasswordError(emptyPassword);
      return;
    }

    setLoading(true);

    try {
      const response = await AuthService.autenticar(email.trim(), password);
      localStorage.setItem(USER, JSON.stringify(response.data.data));
      localStorage.setItem(CLIENT, response.headers[CLIENT]);
      localStorage.setItem(UID, response.headers[UID]);
    } catch (err) {
      setFormError("credentials");
      setLoading(false);
      return;
    }

    try {
      await AuthService.adminProfile();
      history.replace("/admin");
    } catch (err) {
      await AuthService.logout().catch(() => undefined);
      setFormError(isForbidden(err) ? "forbidden" : "unavailable");
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.requestSubmit();
    }
  };

  const formErrorText = {
    credentials: t(`${keyI18n}.invalid`),
    forbidden: t(`${adminI18n}.forbidden`),
    unavailable: t(`${adminI18n}.unavailable`),
  };

  const emailErrorText = emailError ? t(`${keyI18n}.required`) : undefined;
  const passwordErrorText = passwordError
    ? t(`${keyI18n}.required`)
    : formError
    ? formErrorText[formError]
    : undefined;

  return (
    <Layout title={t(`${adminI18n}.title`)}>
      <div className={style.body}>
        <section className={style.card}>
          <header className={style.header}>
            <h1 className={`pgm-headline-small ${style.title}`}>{t(`${adminI18n}.title`)}</h1>
            <p className={`pgm-body-large ${style.subtitle}`}>{t(`${adminI18n}.subtitle`)}</p>
          </header>

          <form className={style.form} onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate>
            <Input
              autoComplete="email"
              autoFocus
              error={emailError || formError !== null}
              errorText={emailErrorText}
              fullWidth
              label={t(`${keyI18n}.email`)}
              leadingIcon={<span className="material-symbols-sharp">mail</span>}
              name="email"
              onInput={(event: MaterialInputEvent) => {
                setEmail(event.target.value);
                if (emailError || formError) {
                  clearErrors();
                }
              }}
              required
              type="email"
              value={email}
              variant="filled"
            />

            <Input
              autoComplete="current-password"
              error={passwordError || formError !== null}
              errorText={passwordErrorText}
              fullWidth
              hideClearButton
              label={t(`${keyI18n}.password`)}
              leadingIcon={<span className="material-symbols-sharp">password</span>}
              name="password"
              onInput={(event: MaterialInputEvent) => {
                setPassword(event.target.value);
                if (passwordError || formError) {
                  clearErrors();
                }
              }}
              required
              trailingIcon={
                <span
                  aria-label={
                    passwordShown ? t(`${keyI18n}.hidePassword`) : t(`${keyI18n}.showPassword`)
                  }
                  className={`material-symbols-sharp ${style.eyeToggle}`}
                  onClick={() => setPasswordShown((value) => !value)}
                  role="button"
                  tabIndex={0}
                >
                  {passwordShown ? "visibility" : "visibility_off"}
                </span>
              }
              type={passwordShown ? "text" : "password"}
              value={password}
              variant="filled"
            />

            <div className={style.actions}>
              <p className={`pgm-body-medium ${style.help}`}>
                {t(`${keyI18n}.help`)}{" "}
                <a className={style.contactLink} href={`mailto:${SUPPORT_EMAIL}`}>
                  {t(`${keyI18n}.contact`)}
                </a>
              </p>

              <Button
                className={style.submit}
                variant="filled"
                shape="round"
                size="small"
                type="submit"
                disabled={loading}
                softDisabled={loading}
                icon={<span className="material-symbols-sharp">login</span>}
              >
                {t(`${keyI18n}.submit`)}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </Layout>
  );
}

function isForbidden(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { error?: string }).error === "forbidden";
}
